import type { Properties } from "posthog-js";

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

export interface PostHogIdentity {
  userId: string;
  email?: string | null;
  tier?: "starter" | "pro" | null;
  isAdmin?: boolean | null;
}

export interface PostHogConfig {
  apiKey: string;
  host: string;
}

export interface PostHogRuntimeEnv {
  prod: boolean;
  apiKey?: string;
  host?: string;
  hostname?: string;
}

// Analytics must only ever leave the real production site. `import.meta.env.PROD`
// alone is not enough: `vite preview` and e2e runs serve a production build on
// localhost:4173 and were emitting events (and minting phantom persons) into the
// production PostHog project. Gate on the serving hostname as well, mirroring the
// inline-snippet gates used by dataviking-site / SynthBench / Althing.
const PRODUCTION_HOSTNAME = "traitprint.com";

export function isProductionHostname(hostname: string | undefined): boolean {
  if (!hostname) return false;
  return hostname === PRODUCTION_HOSTNAME || hostname.endsWith(`.${PRODUCTION_HOSTNAME}`);
}

export function buildPostHogConfig(env: PostHogRuntimeEnv): PostHogConfig | null {
  if (!env.prod) return null;
  if (!isProductionHostname(env.hostname)) return null;

  const apiKey = env.apiKey?.trim();
  if (!apiKey) return null;

  return {
    apiKey,
    host: env.host?.trim() || DEFAULT_POSTHOG_HOST,
  };
}

function getEmailDomain(email?: string | null): string | undefined {
  const domain = email?.trim().split("@")[1]?.trim().toLowerCase();
  return domain || undefined;
}

export function buildPostHogPersonProperties(identity: PostHogIdentity): Properties {
  return {
    account_tier: identity.tier ?? "unknown",
    is_admin: identity.isAdmin ?? false,
    email_domain: getEmailDomain(identity.email),
  };
}

export function buildPostHogSuperProperties(identity: PostHogIdentity): Properties {
  return {
    account_tier: identity.tier ?? "unknown",
    is_admin: identity.isAdmin ?? false,
  };
}

function getPostHogConfig(): PostHogConfig | null {
  return buildPostHogConfig({
    prod: import.meta.env.PROD,
    apiKey: import.meta.env.VITE_POSTHOG_KEY,
    host: import.meta.env.VITE_POSTHOG_HOST,
    hostname: typeof window === "undefined" ? undefined : window.location.hostname,
  });
}

type PostHogClient = typeof import("posthog-js").default;

let posthogClient: PostHogClient | null = null;
let posthogClientPromise: Promise<PostHogClient | null> | null = null;

async function loadPostHogClient(): Promise<PostHogClient | null> {
  if (posthogClient) return posthogClient;
  if (posthogClientPromise) return posthogClientPromise;

  const config = getPostHogConfig();
  if (!config || typeof window === "undefined") return null;

  posthogClientPromise = import("posthog-js")
    .then((mod) => {
      const client = mod.default;
      client.init(config.apiKey, {
        api_host: config.host,
        capture_pageview: false,
        capture_pageleave: false,
      });
      posthogClient = client;
      return client;
    })
    .catch(() => {
      posthogClientPromise = null;
      posthogClient = null;
      return null;
    });

  return posthogClientPromise;
}

export async function initPostHog(): Promise<void> {
  await loadPostHogClient();
}

export async function identifyPostHogUser(
  identity: PostHogIdentity | null | undefined,
): Promise<void> {
  const client = await loadPostHogClient();
  if (!client) return;

  if (!identity?.userId) {
    client.reset();
    return;
  }

  client.identify(identity.userId, buildPostHogPersonProperties(identity));
  client.register(buildPostHogSuperProperties(identity));
}

export async function resetPostHog(): Promise<void> {
  const client = await loadPostHogClient();
  client?.reset();
}

export async function capturePostHogPageview(path: string): Promise<void> {
  const client = await loadPostHogClient();
  if (!client || typeof window === "undefined") return;

  const properties: Properties = {
    $current_url: window.location.href,
    $pathname: path,
  };

  client.capture("$pageview", properties);
}

export async function capturePostHogEvent(
  eventName: string,
  properties: Properties = {},
): Promise<void> {
  const client = await loadPostHogClient();
  if (!client) return;

  client.capture(eventName, properties);
}
